<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'TaskViewModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    task: {
      type: Object,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'lg',
  }));
  const b_4615685607825208_variantMap = computed(() => ({
    PENDING: 'warning',
    STARTED: 'primary',
    FAILURE: 'danger',
  }));
  const b_2095089031469157_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_4615685607825208_variantMap: b_4615685607825208_variantMap.value,
    b_2095089031469157_options: b_2095089031469157_options.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_4615685607825208_variantMap', nodeId: '4615685607825208' },
      { name: 'b_2095089031469157_options', nodeId: '2095089031469157' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_4615685607825208_variantMap,
      b_2095089031469157_options,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Task') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.task,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('task');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_puuhnyf4m_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_puuhnyf4m =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_puuhnyf4m_getActionArgs,
    });
  const inst_evh_7315092382398562_close_cjai1ciri =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_puuhnyf4m,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_cjai1ciri],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_j0g344md8 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_0de8yrjjv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_j0g344md8,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_0de8yrjjv],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_cjai1ciri,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_0de8yrjjv,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_close_cjai1ciri = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_cjai1ciri.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_0de8yrjjv = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_0de8yrjjv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="TaskViewModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="page d-flex flex-column" v-if="loaded">
      <div class="page__content container-fluid pt-3 h-100">
        <ItpBox direction="column">
          <ItpBadge
            :text="task.status"
            :value="task.status"
            :variantMap="b_4615685607825208_variantMap"
          >
          </ItpBadge>
          <ItpCodeMirror
            :options="b_2095089031469157_options"
            v-model="task.result_text"
            :value="task.result_text"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (task.result_text =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            v-if="task.result_text"
          >
          </ItpCodeMirror>
        </ItpBox>
      </div>
    </div>
  </ItpPage>
</template>
